'use client';

import type { FormSchema } from '~/landing/core/simulator/future-savings-planner';

import {
  SimpleCard,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@polyseme/design-system';
import { useTranslations } from 'next-intl';
import { Suspense, useState } from 'react';

import { ContactTab } from './contact-tab';
import { SubscribeTab } from './subscribe-tab';

const tabs = [
  {
    key: 'contactMe',
    content: ({ simulation }: { simulation: FormSchema }) => (
      <ContactTab simulation={simulation} />
    ),
  },
  {
    key: 'subscribe',
    content: SubscribeTab,
  },
] as const;

type TabKeys = (typeof tabs)[number]['key'];

const defaultTab = tabs[0].key;

type ContactCardProps = {
  simulation: FormSchema;
};

export const ContactCard = ({ simulation }: ContactCardProps) => {
  const t = useTranslations('landing.retraite.contactCard');
  const [activeTab, setActiveTab] = useState<TabKeys>(defaultTab);

  const handleTabChange = (value: TabKeys) => setActiveTab(value);

  return (
    <SimpleCard
      className="mx-auto max-w-4xl"
      title={t(`tabs.${activeTab}.title`)}
    >
      <Tabs
        className="w-full"
        defaultValue={defaultTab}
        onValueChange={handleTabChange}
      >
        <TabsList className="w-full">
          {tabs.map(({ key }) => (
            <TabsTrigger key={key} className="w-full" value={key}>
              {t(`tabs.${key}.label`)}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map(({ content: Content, key }) => (
          <TabsContent
            key={key}
            forceMount
            className="mt-6"
            hidden={key !== activeTab}
            value={key}
          >
            <div className="flex size-full items-center justify-center">
              <Suspense>
                <Content simulation={simulation} />
              </Suspense>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </SimpleCard>
  );
};
